import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { OdooConfig, OdooConfigCambio, OdooCredencialAviso, OdooEstadoConexion, OdooPruebaBuzon, OdooRespuesta } from './odoo.models';

/** Un movimiento de la bitácora ya escrito en palabras por el servidor. */
export interface OdooMovimiento {
  _id: string;
  tipo: string;
  source_id: string | null;
  nombre: string | null;
  ok: boolean;
  status: number | null;
  duracion_ms: number | null;
  createdAt: string;
  frase: string;
  nota: string | null;
  quien: string;
  sobre: string;
  motivo: string | null;
  simple: string;
}

/** Lo mínimo para abrir el detalle de un evento (Bitácora, historial del cliente y del comercio). */
export type OdooEventoVista = Pick<OdooMovimiento, '_id' | 'ok' | 'status' | 'duracion_ms' | 'motivo' | 'simple' | 'quien'>;

export interface OdooMovimientosPagina {
  items: OdooMovimiento[];
  total: number;
  con_problema: number;
  page: number;
  limit: number;
}

export interface OdooMovimientosFiltro {
  sobre: '' | 'cliente' | 'comercio' | 'archivo' | 'conexion';
  ok: '' | 'true' | 'false';
  q: string;
  page: number;
  limit: number;
}

export interface OdooDatosTecnicos {
  peticion: unknown;
  respuesta: unknown;
  status: number | null;
  duracion_ms: number | null;
  error: string | null;
}

export interface OdooUltimaPrueba { ok: boolean; mensaje: string; probado_en: string; }

/** Configuración tal como la entrega /config: con la última prueba y la vigencia de la clave. */
export interface OdooConfigVista extends OdooConfig {
  conexion: OdooUltimaPrueba | null;
  credencial_dias: number | null;
  credencial_revisada: string | null;
  credencial_aviso?: OdooCredencialAviso | null;
}

export interface OdooPruebaVista {
  estado: OdooEstadoConexion;
  mensaje: string;
  ms: number;
  credencial_dias?: number | null;
}

export interface OdooGrupoDia { clave: string; titulo: string; items: OdooMovimiento[]; }

const ZONA = 'America/Caracas';
const fClave = new Intl.DateTimeFormat('en-CA', { timeZone: ZONA, year: 'numeric', month: '2-digit', day: '2-digit' });
const fPartes = new Intl.DateTimeFormat('es-VE', { timeZone: ZONA, weekday: 'long', day: 'numeric', month: 'short' });

const mayuscula = (t: string): string => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);

/** «lunes 19-oct» en hora de Venezuela. */
export function diaCorto(d: Date): string {
  const p = fPartes.formatToParts(d);
  const v = (t: string): string => (p.find((x) => x.type === t)?.value || '').replace('.', '');
  return `${v('weekday')} ${v('day')}-${v('month')}`;
}

/** Agrupa una página (ya ordenada) en «Hoy», «Ayer» y luego por fecha. Pura: recibe `ahora`. */
export function agruparPorDia(items: OdooMovimiento[], ahora: Date = new Date()): OdooGrupoDia[] {
  const hoy = fClave.format(ahora);
  const ayer = fClave.format(new Date(ahora.getTime() - 24 * 3600 * 1000));
  const grupos: OdooGrupoDia[] = [];
  const por = new Map<string, OdooGrupoDia>();
  for (const it of items) {
    const d = new Date(it.createdAt);
    if (isNaN(d.getTime())) continue;
    const clave = fClave.format(d);
    let g = por.get(clave);
    if (!g) {
      const largo = diaCorto(d);
      const titulo = clave === hoy ? `Hoy · ${largo}` : clave === ayer ? `Ayer · ${largo}` : mayuscula(largo);
      g = { clave, titulo, items: [] };
      por.set(clave, g);
      grupos.push(g);
    }
    g.items.push(it);
  }
  return grupos;
}

/** Peticiones de las pestañas Bitácora y Configuración (servicio propio para no mezclar con odoo.service). */
@Injectable({ providedIn: 'root' })
export class OdooBitacoraService {
  private readonly base = `${environment.BASE_LA}/api/odoo`;

  constructor(private http: HttpClient) {}

  movimientos(f: OdooMovimientosFiltro): Observable<OdooMovimientosPagina> {
    const params: Record<string, string> = { page: String(f.page), limit: String(f.limit) };
    if (f.sobre) params['sobre'] = f.sobre;
    if (f.ok) params['ok'] = f.ok;
    if (f.q.trim()) params['q'] = f.q.trim();
    return this.http.get<OdooRespuesta<OdooMovimientosPagina>>(`${this.base}/bitacora`, { params }).pipe(map((r) => r.data));
  }

  tecnico(id: string): Observable<OdooDatosTecnicos> {
    return this.http.get<OdooRespuesta<OdooDatosTecnicos>>(`${this.base}/bitacora/${encodeURIComponent(id)}/tecnico`).pipe(map((r) => r.data));
  }

  config(): Observable<OdooConfigVista> {
    return this.http.get<OdooRespuesta<OdooConfigVista>>(`${this.base}/config`).pipe(map((r) => r.data));
  }

  guardarConfig(cambio: OdooConfigCambio): Observable<OdooConfig & { credencial_dias?: number | null; credencial_revisada?: string | null }> {
    return this.http.patch<OdooRespuesta<OdooConfig & { credencial_dias?: number | null; credencial_revisada?: string | null }>>(`${this.base}/config`, cambio).pipe(map((r) => r.data));
  }

  /** Prueba el permiso de subir al buzón sin subir nada. Ruta asumida: POST /api/odoo/buzon/probar → {ok, detalle}. */
  probarBuzon(): Observable<OdooPruebaBuzon> {
    return this.http.post<OdooRespuesta<OdooPruebaBuzon> | OdooPruebaBuzon>(`${this.base}/buzon/probar`, {}).pipe(
      map((r) => ('data' in r && r.data ? r.data : r) as OdooPruebaBuzon),
    );
  }

  probar(): Observable<OdooPruebaVista> {
    return this.http.post<OdooRespuesta<OdooPruebaVista>>(`${this.base}/config/probar`, {}).pipe(map((r) => r.data));
  }
}

